import React, { useState, useEffect, useRef } from "react";
import { FaSearch } from "react-icons/fa";
import Header from "../Common/AdminHeader";
import axios from "axios";
import { Config } from "../../../API/config";
import { TrainerData } from "../../../types/TrainerInfo";
import toast from "react-hot-toast";
import AdminTrainerCard from "../../../Shared components/AdminTrainerCard";
import TrainerViewCard from "../../../Shared components/TrainerViewCard";
import { ExternalLink, UserCheck, X } from "lucide-react";
import { motion } from "framer-motion";

interface User {
    _id: string;
    name: string;
    email: string;
    profileImage: string;
    phone: string;
    block: boolean;
    createdAt: string;
}
//manage Trainers page
const AnalyticsPage = () => {
    const [trainersByRate, setTrainersByRate] = useState<TrainerData[]>([]);
    const [trainers, setTrainers] = useState<User[]>([]);
    const [showTrainerCard, setShowTrainerCard] = useState(false);
    const [selectedTrainerId, setSelectedTrainerId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [buttonLoading, setButtonLoading] = useState<string | null>(null); // New state for button loading
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const dialogRef = useRef<HTMLDivElement>(null);

    const fetchAllTrainers = async (pageNum: number) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${Config.getAllTrainersAdmin}?page=${pageNum}`, {
                withCredentials: true
            });

            if (data) {
                setTrainers(data);
                setTotalPages(data.pagination?.totalPages || 1);
            }
        } catch (err) {
            toast.error("حدث خطأ أثناء جلب المدربين");
            console.error("Error fetching trainers:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchHighRatedTrainers = async () => {
        try {
            const { data } = await axios.get(Config.getTrainersSortedByRate, { withCredentials: true });
            if (data.statusCode === 200) {
                const formattedTrainers = data.data.map((trainer: TrainerData) => ({
                    ...trainer,
                    trainerInfo: {
                        ...trainer.trainerInfo,
                        rate: Number(trainer.trainerInfo.rate.toFixed(1))
                    }
                }));
                setTrainersByRate(formattedTrainers);
            }
        } catch (err) {
            toast.error("حدث خطأ أثناء جلب المدربين الأعلى تقييمًا");
            console.error("Error fetching high rated trainers:", err);
        }
    };

    const downloadExcelSheet = async () => {
        try {
            const response = await axios.get(Config.downloadTrainers, {
                withCredentials: true,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Trainers.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success("تم تحميل ملف Excel بنجاح");
        } catch (err) {
            toast.error("حدث خطأ أثناء تحميل ملف Excel");
            console.error("Error downloading Excel sheet:", err);
        }
    };

    const blockTrainer = async (trainerId: string) => {
        setButtonLoading(trainerId); // Set loading for this specific trainer
        try {
            const trainer = trainers.find(t => t._id === trainerId);
            const currentBlockStatus = trainer?.block;

            const { data } = await axios.patch(
                `${Config.BlockTrainer}/${trainerId}`,
                {},
                { withCredentials: true }
            );

            if (data) {
                setTrainers(prev =>
                    prev.map(t =>
                        t._id === trainerId ? { ...t, block: !currentBlockStatus } : t
                    )
                );
                toast.success(currentBlockStatus ? "تم فك حظر المدرب" : "تم حظر المدرب");
            }
        } catch (err) {
            toast.error("فشل في تعديل حالة المدرب");
            console.error("Error updating trainer status:", err);
        } finally {
            setButtonLoading(null); // Reset loading state
        }
    };

    useEffect(() => {
        fetchAllTrainers(page);
        fetchHighRatedTrainers();
    }, [page]);

    const handleClickOutside = (event: MouseEvent) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
            setShowTrainerCard(false);
            setSelectedTrainerId(null);
        }
    };

    useEffect(() => {
        if (showTrainerCard) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showTrainerCard]);

    const filteredTrainers = trainers.filter(trainer =>
        trainer.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleViewTrainer = (trainerId: string) => {
        setSelectedTrainerId(trainerId);
        setShowTrainerCard(true);
    };

    return (
        <div className="flex-1 overflow-auto relative z-10 bg-white">
            <Header title={" ادارة المدربين"} />

            <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8">
                <div className="my-7">
                    <h2 className="text-xl font-bold text-center mb-4"> المدربين الأعلى تقييمًا</h2>
                    <div className="flex flex-col md:flex-row flex-wrap justify-center gap-4">
                        {trainersByRate.slice(0, 3).map((trainer) => (
                            <AdminTrainerCard key={trainer._id} Trainer={trainer} />
                        ))}
                    </div>
                </div>

                <div className="relative mb-4 flex justify-center">
                    <div className="relative w-full md:w-1/2">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                        <input
                            type="text"
                            placeholder="ابحث عن مدرب..."
                            className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="bg-white border rounded-lg p-4 w-full max-w-3xl mx-auto mt-6">
                    <div className="flex justify-between items-center border-b pb-3">
                        <div className="flex items-center space-x-reverse space-x-2">
                            <UserCheck size={20} className="text-gray-700" />
                            <h2 className="text-lg font-semibold text-gray-900">جميع المدربين</h2>
                        </div>
                        <button
                            onClick={downloadExcelSheet}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                        >
                            تحميل ملف Excel
                        </button>
                    </div>

                    {loading ? (
                        <p className="text-center text-gray-600 py-6">جارٍ التحميل...</p>
                    ) : (
                        <div className="mt-4 space-y-4">
                            {filteredTrainers.map((trainer) => (
                                <div key={trainer._id} className="flex flex-row-reverse items-center justify-between p-2 bg-gray-100 rounded-lg">
                                    <div className="flex flex-row-reverse items-center space-x-reverse space-x-3">
                                        <img src={trainer.profileImage} alt={trainer.name} className="w-10 h-10 rounded-full" />
                                        <div className="text-right">
                                            <p className="text-gray-900 font-medium">{trainer.name}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-reverse space-x-2">
                                        <ExternalLink
                                            size={18}
                                            className="text-gray-500 cursor-pointer mx-3"
                                            onClick={() => handleViewTrainer(trainer._id)}
                                        />
                                        <button
                                            onClick={() => blockTrainer(trainer._id)}
                                            disabled={buttonLoading === trainer._id}
                                            className={`px-3 py-1 rounded-lg transition text-white ${trainer.block
                                                    ? 'bg-green-500 hover:bg-green-600'
                                                    : 'bg-red-500 hover:bg-red-600'
                                                } ${buttonLoading === trainer._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {buttonLoading === trainer._id ? '...جارٍ' : (trainer.block ? 'فك الحظر' : 'حظر')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && (
                        <div className="mt-4 flex justify-center gap-4">
                            <button
                                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                disabled={page === 1}
                                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
                            >
                                السابق
                            </button>
                            <span className="py-2">صفحة {page} من {totalPages}</span>
                            <button
                                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={page === totalPages}
                                className="px-4 py-2 bg-gray-200 rounded-lg disabled:opacity-50"
                            >
                                التالي
                            </button>
                        </div>
                    )}
                </div>
            </main>

            {showTrainerCard && selectedTrainerId && (
                <motion.div
                    initial={{ x: "-100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "-100%" }}
                    transition={{ duration: 0.5 }}
                    className="fixed top-0 left-0 w-96 h-full bg-white shadow-lg p-4 overflow-y-auto"
                >
                    <button className="absolute top-2 right-2" onClick={() => setShowTrainerCard(false)}>
                        <X size={20} />
                    </button>
                    <TrainerViewCard id={selectedTrainerId} />
                </motion.div>
            )}
        </div>
    );
};

export default AnalyticsPage;